'use client'

import { useMemo, useState } from 'react'

import { useTranslations } from 'next-intl'

import {
  Chip,
  FeatureCard,
  type FeatureCardData,
} from '@/components/business/voice/shared'
import { useRouter } from '@/i18n/navigation'
import { useAudioPubList, useAudioTagList } from '@/lib/api/hooks/audio'
import { cn } from '@/lib/utils/cn'
import { pubItemToPlazaTrack } from '@/lib/voice/plaza-utils'
import { isQueryLoading } from '@/lib/voice/query-loading'

import { PlazaCommunityList } from './plaza-community-list'
import { VoiceCloneArt, VoiceDesignArt } from './plaza-feature-art'

/**
 * Plaza（Discover）页面
 * - Hero 大标语
 * - 2 个 FeatureCard 入口（跳转 Voice Design / Voice Clone）
 * - Community 标签栏（GET /v1/audio/tag_list）+ 音频列表（GET /v1/audio/pub_list）
 */
export const PlazaPage = () => {
  const t = useTranslations('voice.plaza')
  const router = useRouter()

  /** null = 全部（不传 tag_id） */
  const [selectedTagId, setSelectedTagId] = useState<number | null>(null)
  const [view, setView] = useState<'library' | 'community'>('community')

  const tagQuery = useAudioTagList()
  const tagList = tagQuery.data?.list ?? []
  const tagLoading = isQueryLoading(tagQuery)

  const { list, query } = useAudioPubList({
    tagId: selectedTagId ?? undefined,
    enabled: view === 'community',
  })

  const tracks = useMemo(() => list.map(pubItemToPlazaTrack), [list])

  const handleSelectTag = (tagId: number | null) => {
    setSelectedTagId(tagId)
  }

  const features: FeatureCardData[] = [
    {
      id: 'voice-design',
      title: t('feature.design.title'),
      subtitle: t('feature.design.subtitle'),
      accent: '#38bdf8',
      accentSoft: 'rgba(56,189,248,0.42)',
      tint: 'rgba(56,189,248,0.06)',
      art: <VoiceDesignArt />,
    },
    {
      id: 'voice-clone',
      title: t('feature.clone.title'),
      subtitle: t('feature.clone.subtitle'),
      accent: '#c084fc',
      accentSoft: 'rgba(192,132,252,0.45)',
      tint: 'rgba(192,132,252,0.06)',
      art: <VoiceCloneArt />,
    },
  ]

  return (
    <div className="relative z-[1] mx-auto max-w-[1480px] px-4 pb-16 pt-[72px] sm:px-6 sm:pb-20 md:px-9">
      {/* Hero */}
      <div className="mb-6 pt-4 md:mb-9 md:pt-8">
        <h1 className="voice-display m-0 text-[32px] font-medium leading-[1.1] tracking-[-0.03em] text-[var(--text-1)]">
          {t('hero')}
        </h1>
      </div>

      {/* Feature cards */}
      <div className="mb-8 grid w-full grid-cols-2 gap-3 md:mb-10 md:gap-4">
        {features.map(f => (
          <FeatureCard
            key={f.id}
            feature={f}
            onClick={() => router.push(f.id === 'voice-design' ? '/voice/design' : '/voice/clone')}
          />
        ))}
      </div>

      {/* Library / Community */}
      <div className="mb-5">
        <div className="mb-4 flex items-center gap-5 md:mb-[18px] md:gap-7">
          {(
            [
              { id: 'community', label: t('view.community') },
            ] as const
          ).map(v => (
            <button
              key={v.id}
              type="button"
              className={cn(
                "relative cursor-pointer border-0 bg-transparent px-0 py-1 text-base font-semibold tracking-[-0.01em] transition-colors duration-200 [font-family:'Sora',sans-serif] md:text-lg",
                view === v.id ? 'text-[var(--text-1)]' : 'text-[var(--text-4)]'
              )}
              onClick={() => setView(v.id)}
            >
              {v.label}
              {/* 下划线指示器：当前只有一个 tab，先注释；如需多 tab 切换时再放开 */}
              {/* {view === v.id && (
                <div className="absolute -bottom-2 left-0 right-0 h-0.5 rounded-sm bg-white" />
              )} */}
            </button>
          ))}
        </div>

        {view === 'community' && (
          <div className="flex flex-col items-stretch gap-3 lg:flex-row lg:items-center lg:justify-between">
            <div className="voice-scroll-hide -mx-4 flex gap-2 overflow-x-auto px-4 lg:mx-0 lg:flex-1 lg:flex-wrap lg:overflow-visible lg:px-0">
              <Chip
                active={selectedTagId === null}
                onClick={() => handleSelectTag(null)}
              >
                {t('tabs.all')}
              </Chip>
              {tagLoading && (
                <span className="self-center px-2 text-[12px] text-[var(--text-4)]">
                  {t('loadingList')}
                </span>
              )}
              {tagList.map(tag => (
                <Chip
                  key={tag.id}
                  active={selectedTagId === tag.id}
                  hint={tag.description}
                  onClick={() => handleSelectTag(tag.id)}
                >
                  {tag.name}
                </Chip>
              ))}
            </div>
          </div>
        )}
      </div>

      {view === 'community' && <PlazaCommunityList query={query} tracks={tracks} />}
    </div>
  )
}
